import React, { useEffect, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import Swal from "sweetalert2";
import request from "../../../../Helpers/API";
import PlantillaScreen from "../../../helpers/PlantillaScreen2";
import { LoaderScreen } from "../../../components/LoaderScreen";
import SaveIcon from "@mui/icons-material/Save";
import SettingsInputComponentIcon from "@mui/icons-material/SettingsInputComponent";
import CloseIcon from "@mui/icons-material/Close";

// Modal de Alta/Edición del catálogo Parte-Multiplicador (Postventa /
// Reporte de Refacciones / Multiplicadores de Piezas). Un solo modal para
// ambos casos: sin `registro` es alta (POST con idlistado -99), con
// `registro` es edición (POST con el idlistado real). Sigue la convención
// ram-modal del proyecto, igual que VendedoresEquipModal.js.
//
// Props:
// - open: boolean.
// - registro: { idlistado, parte, multiplicador } o null/undefined si es alta.
// - onClose: fn().
// - onGuardado: fn() opcional, se llama después de guardar con éxito.
export const MultiplicadorPiezaModal = ({ open, registro, onClose, onGuardado }) => {
    const esEdicion = !!registro;

    const [catalogoPartes, setCatalogoPartes] = useState([]);
    const [parte, setParte] = useState("");
    const [multiplicador, setMultiplicador] = useState("");
    const [loader, setLoader] = useState({
        mostrar: false,
        alerta: false,
        error: false,
        mensaje: "",
    });

    // Al abrir: precarga los valores del registro (si es edición) y trae el
    // catálogo de números de parte para el Autocomplete.
    useEffect(() => {
        if (!open) return;

        setParte(registro?.parte || "");
        setMultiplicador(
            registro?.multiplicador !== undefined && registro?.multiplicador !== null
                ? String(registro.multiplicador)
                : ""
        );

        setLoader((prev) => ({ ...prev, mostrar: true }));
        request
            .get("MultiplicadorPieza/partes")
            .then((result) => {
                setCatalogoPartes(result || []);
                setLoader((prev) => ({ ...prev, mostrar: false }));
            })
            .catch(() => {
                setLoader((prev) => ({ ...prev, mostrar: false }));
                Swal.fire(
                    "MULTIPLICADOR DE PIEZAS",
                    "No fue posible cargar el catálogo de piezas. Intente nuevamente o contacte al administrador.",
                    "error"
                );
            });
    }, [open, registro]);

    if (!open) return null;

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) onClose?.();
    };

    const onGuardar = () => {
        if (!parte) {
            Swal.fire("MULTIPLICADOR DE PIEZAS", "Seleccione una parte.", "warning");
            return;
        }

        const multiplicadorNum = Number(multiplicador);
        if (!multiplicador || Number.isNaN(multiplicadorNum) || multiplicadorNum <= 0) {
            Swal.fire("MULTIPLICADOR DE PIEZAS", "Indique un multiplicador numérico mayor a 0.", "warning");
            return;
        }

        const post = {
            idparte: esEdicion ? registro.idlistado : -99,
            parte,
            multiplicador: multiplicadorNum,
        };

        setLoader((prev) => ({ ...prev, mostrar: true }));
        request
            .post("MultiplicadorPieza/Guardar", post)
            .then(async () => {
                setLoader((prev) => ({ ...prev, mostrar: false }));
                await Swal.fire("MULTIPLICADOR DE PIEZAS", "Registro guardado con éxito.", "success");
                onGuardado?.();
                onClose?.();
            })
            .catch(() => {
                setLoader((prev) => ({ ...prev, mostrar: false }));
                Swal.fire(
                    "MULTIPLICADOR DE PIEZAS",
                    "No fue posible guardar el registro. Intente nuevamente o contacte al administrador.",
                    "error"
                );
            });
    };

    return (
        <div className="ram-backdrop">
            <PlantillaScreen
                titulo={esEdicion ? "Editar multiplicador de pieza" : "Agregar multiplicador de pieza"}
                className={"ram-modal"}
            >
                <button className="ram-close" onClick={onClose} type="button">
                    <CloseIcon />
                </button>
                <LoaderScreen loader={loader} />

                <div className="ram-body">
                    {/* Parte: selección obligatoria del catálogo (no admite texto libre). */}
                    <div className="ram-field">
                        <div className="ram-field__icon">
                            <SettingsInputComponentIcon />
                        </div>
                        <div className="ram-field__content">
                            <Autocomplete
                                size="small"
                                options={catalogoPartes}
                                value={parte || null}
                                onChange={(_, newValue) => setParte(newValue || "")}
                                renderInput={(params) => (
                                    <TextField {...params} label="Parte" placeholder="Buscar número de parte..." />
                                )}
                            />
                        </div>
                    </div>

                    {/* Multiplicador: valor numérico libre, no hay catálogo para este campo. */}
                    <div className="ram-field">
                        <div className="ram-field__icon">
                            <span className="ram-field__icon-texto">x</span>
                        </div>
                        <div className="ram-field__content">
                            <TextField
                                size="small"
                                fullWidth
                                label="Multiplicador"
                                type="number"
                                inputProps={{ min: 0, step: "any" }}
                                value={multiplicador}
                                onChange={({ target }) => setMultiplicador(target.value)}
                            />
                        </div>
                    </div>
                </div>

                <div className="ram-footer">
                    <button
                        type="button"
                        className="ram-btn ram-btn--primary"
                        onClick={onGuardar}
                        disabled={loader.mostrar}
                    >
                        <SaveIcon />
                        Guardar
                    </button>
                </div>
            </PlantillaScreen>
        </div>
    );
};

export default MultiplicadorPiezaModal;
